import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Database,
  Download,
  Edit,
  Eye,
  EyeOff,
  Plus,
  Search,
  Settings,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfigurationDrawer from '../components/dropdown-admin/ConfigurationDrawer';
import OptionsDrawer from '../components/dropdown-admin/OptionsDrawer';
import { Dropdown } from '../components/forms';
import { Configuration, DropdownOption } from '../services/configurationService';
import { dropdownAdminService } from '../services/dropdownAdminService';

interface FilterState {
  type: string;
  category: string;
  search: string;
  status: 'all' | 'active' | 'inactive';
}

const DropdownAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfigs, setSelectedConfigs] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FilterState>({
    type: 'dropdown',
    category: '',
    search: '',
    status: 'all',
  });
  const [expandedConfigs, setExpandedConfigs] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Configuration | null>(null);
  const [showOptionsModal, setShowOptionsModal] = useState<{
    config: Configuration;
    editingOption?: DropdownOption;
  } | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configsData, categoriesData] = await Promise.all([
        dropdownAdminService.getAllConfigurations({
          type: filters.type || undefined,
          category: filters.category || undefined,
          search: filters.search || undefined,
        }),
        dropdownAdminService.getConfigurationCategories(),
      ]);

      let filteredConfigs = configsData;
      if (filters.status !== 'all') {
        filteredConfigs = configsData.filter(config =>
          filters.status === 'active' ? config.isActive : !config.isActive
        );
      }

      setConfigurations(filteredConfigs);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Failed to load configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (key: string) => {
    try {
      await dropdownAdminService.toggleConfigurationStatus(key);
      await loadData();
      setMessage({ type: 'success', text: 'Status updated successfully' });
    } catch (error) {
      console.error('Error toggling status:', error);
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  const handleDeleteConfig = async (key: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await dropdownAdminService.deleteConfiguration(key);
      await loadData();
      setMessage({ type: 'success', text: 'Configuration deleted successfully' });
    } catch (error) {
      console.error('Error deleting configuration:', error);
      setMessage({ type: 'error', text: 'Failed to delete configuration' });
    }
  };

  const handleBulkToggleStatus = async () => {
    if (selectedConfigs.size === 0) return;

    try {
      const operations = Array.from(selectedConfigs).map(key => ({
        key,
        action: 'toggleStatus' as const,
      }));

      await dropdownAdminService.bulkUpdateConfigurations(operations);
      await loadData();
      setSelectedConfigs(new Set());
      setMessage({ type: 'success', text: 'Bulk status update completed' });
    } catch (error) {
      console.error('Error in bulk update:', error);
      setMessage({ type: 'error', text: 'Failed to update configurations' });
    }
  };

  const handleExportConfig = (config: Configuration) => {
    const jsonData = dropdownAdminService.exportConfiguration(config);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.key}-config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDuplicateConfig = async (config: Configuration) => {
    const newKey = prompt('Enter new key for duplicated configuration:');
    const newName = prompt('Enter new name for duplicated configuration:');

    if (!newKey || !newName) return;

    try {
      await dropdownAdminService.duplicateConfiguration(config.key, newKey, newName);
      await loadData();
      setMessage({ type: 'success', text: 'Configuration duplicated successfully' });
    } catch (error) {
      console.error('Error duplicating configuration:', error);
      setMessage({ type: 'error', text: 'Failed to duplicate configuration' });
    }
  };

  const toggleConfigExpansion = (key: string) => {
    const newExpanded = new Set(expandedConfigs);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedConfigs(newExpanded);
  };

  const toggleSelectConfig = (key: string) => {
    const newSelected = new Set(selectedConfigs);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedConfigs(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedConfigs.size === configurations.length) {
      setSelectedConfigs(new Set());
    } else {
      setSelectedConfigs(new Set(configurations.map(c => c.key)));
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'text-green-600 bg-green-50' : 'text-gray-500 bg-gray-50';
  };

  const getCategoryColor = (category: string) => {
    const colors = [
      'bg-brand-100 text-brand-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-red-100 text-red-700',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800',
    ];
    const index = category.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Dropdown Management</h1>
            <p className="text-sm text-gray-600">
              Manage system dropdown configurations and options
            </p>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`rounded-lg p-4 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search configurations..."
              value={filters.search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <Dropdown
            label=""
            placeholder="All Types"
            options={[
              { value: '', label: 'All Types' },
              { value: 'dropdown', label: 'Dropdowns' },
              { value: 'tools', label: 'Tools' },
              { value: 'directories', label: 'Directories' },
              { value: 'settings', label: 'Settings' },
            ]}
            value={filters.type}
            onChange={value => setFilters(prev => ({ ...prev, type: value }))}
          />

          <Dropdown
            label=""
            placeholder="All Categories"
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map(cat => ({
                value: cat,
                label: cat.charAt(0).toUpperCase() + cat.slice(1),
              })),
            ]}
            value={filters.category}
            onChange={value => setFilters(prev => ({ ...prev, category: value }))}
          />

          <Dropdown
            label=""
            placeholder="All Status"
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active Only' },
              { value: 'inactive', label: 'Inactive Only' },
            ]}
            value={filters.status}
            onChange={value => setFilters(prev => ({ ...prev, status: value as any }))}
          />
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedConfigs.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-red-800 font-medium">
              {selectedConfigs.size} configuration(s) selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={handleBulkToggleStatus}
                className="px-3 py-1 text-sm text-red-600 hover:bg-red-100 rounded-md transition-colors"
              >
                Toggle Status
              </button>
              <button
                onClick={() => setSelectedConfigs(new Set())}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configurations List */}
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Configurations ({configurations.length})
              </h3>
              <label className="flex items-center space-x-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={
                    selectedConfigs.size === configurations.length && configurations.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                />
                <span>Select All</span>
              </label>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {configurations.map(config => (
              <div key={config.key} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedConfigs.has(config.key)}
                      onChange={() => toggleSelectConfig(config.key)}
                      className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                    />

                    <button
                      onClick={() => toggleConfigExpansion(config.key)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      {expandedConfigs.has(config.key) ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </button>

                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-medium text-gray-900">{config.name}</h4>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(config.category)}`}
                        >
                          {config.category}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(config.isActive)}`}
                        >
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {config.isSystem && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            System
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>
                          Key:{' '}
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {config.key}
                          </code>
                        </span>
                        <span>Type: {config.type}</span>
                        {config.type === 'dropdown' && (
                          <span>Options: {config.options?.length || 0}</span>
                        )}
                      </div>
                      {config.description && (
                        <p className="mt-2 text-sm text-gray-600">{config.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleStatus(config.key)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={config.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {config.isActive ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>

                    <button
                      onClick={() => setEditingConfig(config)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    {config.type === 'dropdown' && (
                      <button
                        onClick={() => setShowOptionsModal({ config })}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Manage Options"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    )}

                    <button
                      onClick={() => handleExportConfig(config)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Export"
                    >
                      <Download className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDuplicateConfig(config)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>

                    {!config.isSystem && (
                      <button
                        onClick={() => handleDeleteConfig(config.key)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded Options View */}
                {expandedConfigs.has(config.key) && config.type === 'dropdown' && (
                  <div className="mt-4 ml-12 p-4 bg-gray-50 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Dropdown Options</h5>
                    <div className="space-y-2">
                      {config.options?.map((option, index) => (
                        <div
                          key={option.value}
                          className="flex items-center justify-between p-2 bg-white rounded border"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-xs text-gray-500 w-8">#{index + 1}</span>
                            <div>
                              <span className="text-sm font-medium">{option.label}</span>
                              <span className="text-xs text-gray-500 ml-2">({option.value})</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getStatusColor(option.isActive)}`}
                            >
                              {option.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <button
                              onClick={() => setShowOptionsModal({ config, editingOption: option })}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              title="Edit Option"
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {configurations.length === 0 && (
              <div className="px-6 py-12 text-center">
                <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations found</h3>
                <p className="text-gray-600 mb-4">
                  Get started by creating your first configuration.
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Configuration
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Configuration Drawer */}
      <ConfigurationDrawer
        isOpen={showCreateModal || !!editingConfig}
        onClose={() => {
          setShowCreateModal(false);
          setEditingConfig(null);
        }}
        onSave={loadData}
        config={editingConfig}
        categories={categories}
      />

      {/* Options Management Drawer */}
      {showOptionsModal && (
        <OptionsDrawer
          isOpen={true}
          onClose={() => setShowOptionsModal(null)}
          onSave={loadData}
          config={showOptionsModal.config}
          editingOption={showOptionsModal.editingOption}
        />
      )}
    </div>
  );
};

export default DropdownAdmin;
